// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { ChevronDown, ChevronRight, BarChart3, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';

const fmt = (n) => (n ?? 0).toLocaleString('it-IT', { maximumFractionDigits: 0 });

// Map instrument to one of: Azionario / Obbligazionario / Liquidità / Crypto / Altro / excluded (Azioni singole)
const categorize = (inst) => {
  const bench = inst.historical_benchmark || '';
  const type = (inst.type || '').toLowerCase();
  if (bench === 'Azioni singole') return { bucket: 'Azionario (singole)', excluded: true };
  if (bench === 'Bitcoin' || bench === 'Crypto' || type.includes('crypto')) {
    // Keep the instrument-specific bench if it's Bitcoin/Ethereum separately below
    return { bucket: 'Crypto', excluded: false };
  }
  if (bench === 'Oro' || bench === 'Commodity' || bench === 'REIT') return { bucket: 'Altro', excluded: false };
  if (bench === 'Liquidità') return { bucket: 'Liquidità', excluded: false };
  if (bench.includes('Obbligazion') || bench === 'BTP Italia') return { bucket: 'Obbligazionario', excluded: false };
  // Default: azionario
  return { bucket: 'Azionario', excluded: false };
};

const lookupHistorical = (inst, historicalReturns) => {
  if (!historicalReturns) return null;
  const bench = inst.historical_benchmark || '';
  // Prefer ticker-specific entries (Bitcoin / Ethereum)
  if (inst.ticker === 'BTC' || (inst.name && inst.name.includes('Bitcoin'))) {
    return { ...historicalReturns['Bitcoin'], key: 'Bitcoin' };
  }
  if (inst.ticker === 'ETH' || (inst.name && inst.name.includes('Ethereum'))) {
    return { ...historicalReturns['Ethereum'], key: 'Ethereum' };
  }
  const direct = historicalReturns[bench];
  if (direct) return { ...direct, key: bench };
  // "Azioni singole" → CAGR null → excluded
  return null;
};

export const HistoricalAnalysisBox = ({ portfolio, analytics, historicalReturns, startYear, setStartYear, amount, setAmount, isOpen, onToggle, currentYear }) => {
  // Deployment 22 — Sezione G: toggle rendimento reale
  const [realReturn, setRealReturn] = React.useState(false);
  const INFLATION_RATE = 0.02;
  const LIQUIDITA_RATE = 0.02;
  // Derive max startYear and min for Bitcoin/Ethereum
  const { minStartYear, maxStartYear, eligible, excludedCount, allSingleStocks, btcPresent, ethPresent } = useMemo(() => {
    if (!portfolio.length) return { minStartYear: 2004, maxStartYear: 2019, eligible: [], excludedCount: 0, allSingleStocks: false, btcPresent: false, ethPresent: false };
    let minYear = 2004; // earliest data
    let maxYear = 2019; // so we have at least 5 years
    const eligible = [];
    let excludedCount = 0;
    let btcPresent = false, ethPresent = false;
    portfolio.forEach(p => {
      const cat = categorize(p.inst);
      const hist = lookupHistorical(p.inst, historicalReturns);
      if (cat.excluded || !hist || hist.cagr == null) {
        excludedCount += 1;
        return;
      }
      if (hist.key === 'Bitcoin') btcPresent = true;
      if (hist.key === 'Ethereum') ethPresent = true;
      if (hist.start_year && hist.start_year > minYear) minYear = hist.start_year;
      eligible.push({ p, cat, hist });
    });
    const allSingleStocks = eligible.length === 0 && excludedCount > 0;
    return { minStartYear: minYear, maxStartYear: maxYear, eligible, excludedCount, allSingleStocks, btcPresent, ethPresent };
  }, [portfolio, historicalReturns]);

  // Clamp start year
  const effectiveStartYear = Math.max(minStartYear, Math.min(maxStartYear + 5, startYear));

  const chartData = useMemo(() => {
    if (!eligible.length) return null;
    const totalEligible = eligible.reduce((s, e) => s + e.p.amount, 0);
    if (totalEligible === 0) return null;
    // Weighted CAGR
    let wCagr = 0;
    let minEligibleYear = effectiveStartYear;
    eligible.forEach(e => {
      const w = e.p.amount / totalEligible;
      wCagr += w * e.hist.cagr;
      // Bitcoin/Ethereum limited to max 5 years
      if (e.hist.max_years) {
        const limit = currentYear - e.hist.max_years;
        if (minEligibleYear < limit) minEligibleYear = limit;
      }
    });
    // Deployment 22 — Sezione G: rendimento reale = (1+nom)/(1+infl) - 1
    const effectiveCagr = realReturn
      ? (1 + wCagr) / (1 + INFLATION_RATE) - 1
      : wCagr;
    const years = [];
    const vals = [];        // Linea 1: portafoglio investito
    const inflVals = [];    // Linea 2: erosione inflazione (Sezione H)
    const liqVals = [];     // Linea 3: conto deposito (Sezione H)
    for (let y = minEligibleYear; y <= currentYear; y++) {
      const t = y - minEligibleYear;
      years.push(String(y));
      vals.push(amount * Math.pow(1 + effectiveCagr, t));
      // Erosione inflazione: capitale che perde potere d'acquisto
      inflVals.push(amount * Math.pow(1 - INFLATION_RATE, t));
      // Conto deposito a tasso costante 2%
      liqVals.push(amount * Math.pow(1 + LIQUIDITA_RATE, t));
    }
    return { years, vals, inflVals, liqVals, wCagr, effectiveCagr, effectiveStart: minEligibleYear };
  }, [eligible, amount, effectiveStartYear, currentYear, realReturn]);

  // Empty states
  if (!portfolio.length) {
    return (
      <CollapsibleBox title="📊 Analisi storica" isOpen={isOpen} onToggle={onToggle}>
        <p className="text-sm text-[#888780] text-center py-6" data-testid="hist-empty">
          Aggiungi strumenti al portafoglio per vedere l'analisi storica.
        </p>
      </CollapsibleBox>
    );
  }
  if (allSingleStocks) {
    return (
      <CollapsibleBox title="📊 Analisi storica" isOpen={isOpen} onToggle={onToggle}>
        <p className="text-sm text-[#888780] text-center py-6" data-testid="hist-empty-stocks">
          Il grafico storico non è disponibile per portafogli composti esclusivamente da azioni singole.
        </p>
      </CollapsibleBox>
    );
  }
  if (!historicalReturns) {
    return (
      <CollapsibleBox title="📊 Analisi storica" isOpen={isOpen} onToggle={onToggle}>
        <p className="text-sm text-[#888780] text-center py-6" data-testid="hist-empty-api">
          Dati storici temporaneamente non disponibili — riprova più tardi.
        </p>
      </CollapsibleBox>
    );
  }

  const finalValue = chartData?.vals[chartData.vals.length - 1] ?? 0;
  const nominalCagrPct = ((chartData?.wCagr ?? 0) * 100).toFixed(1);
  const realCagrPct = (((chartData?.effectiveCagr ?? chartData?.wCagr ?? 0)) * 100).toFixed(1);
  const realCagrPctNet = ((chartData?.wCagr ?? 0) * 100 - 2).toFixed(1);

  return (
    <CollapsibleBox title={`📊 Analisi storica: ${chartData?.effectiveStart ?? effectiveStartYear}–${currentYear}`} isOpen={isOpen} onToggle={onToggle} testid="hist-box">
      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <label className="block">
          <span className="text-xs text-[#888780]">Anno di inizio</span>
          <input
            type="number" step={1} min={minStartYear} max={currentYear - 1}
            value={startYear}
            onChange={e => setStartYear(parseInt(e.target.value, 10) || minStartYear)}
            className="w-full mt-1 bg-[#1C1C1A] border border-white/10 rounded px-3 py-2 text-[#E8E8E4] text-sm"
            data-testid="hist-start-year"
          />
        </label>
        <label className="block">
          <span className="text-xs text-[#888780]">Importo (€)</span>
          <input
            type="number" step={500} min={100}
            value={amount}
            onChange={e => setAmount(parseFloat(e.target.value) || 0)}
            className="w-full mt-1 bg-[#1C1C1A] border border-white/10 rounded px-3 py-2 text-[#E8E8E4] text-sm"
            data-testid="hist-amount"
          />
        </label>
      </div>

      {/* Deployment 22 — Sezione G: toggle rendimento reale */}
      <div className="flex items-center gap-2 mb-3">
        <button
          type="button"
          onClick={() => setRealReturn(v => !v)}
          className="text-xs px-3 py-1.5 rounded-lg transition-colors"
          style={{
            background: realReturn ? 'rgba(201,168,76,0.18)' : 'rgba(255,255,255,0.04)',
            border: `1px solid ${realReturn ? 'rgba(201,168,76,0.4)' : 'rgba(255,255,255,0.08)'}`,
            color: realReturn ? '#C9A84C' : '#888780',
          }}
          data-testid="hist-real-return-toggle"
          aria-pressed={realReturn}
        >
          {realReturn ? '✓ Rendimento reale' : 'Rendimento reale'}
        </button>
        <span className="text-[10px] text-[#888780]">
          {realReturn ? 'al netto inflazione 2%/anno' : 'mostra in nominale'}
        </span>
      </div>

      {startYear > 2019 && (
        <div className="text-xs text-[#E8903A] mb-3" data-testid="hist-warn-short">
          ⚠️ Per una valutazione significativa si consiglia un orizzonte di almeno 5 anni.
        </div>
      )}
      {(btcPresent || ethPresent) && (
        <div className="text-xs text-[#888780] mb-3 italic">
          Per Bitcoin ed Ethereum il grafico mostra gli ultimi 5 anni disponibili — la volatilità storica su orizzonti più lunghi è estrema e non rappresentativa.
        </div>
      )}
      {excludedCount > 0 && (
        <div className="text-xs text-[#888780] mb-3 italic" data-testid="hist-excluded-note">
          Le azioni singole sono escluse dal calcolo storico — i dati storici per singole azioni non sono inclusi in questo strumento educativo.
        </div>
      )}

      {chartData && (
        <>
          <div style={{ height: 280 }}>
            <Line
              data={{
                labels: chartData.years,
                datasets: [
                  {
                    label: realReturn
                      ? `Portafoglio investito (CAGR reale ${realCagrPct}%)`
                      : `Portafoglio investito (CAGR ${nominalCagrPct}%)`,
                    data: chartData.vals,
                    borderColor: '#2e7d32',
                    backgroundColor: 'rgba(46,125,50,0.10)',
                    tension: 0.3,
                    fill: false,
                    borderWidth: 2,
                    pointRadius: 2,
                  },
                  // Deployment 22 — Sezione H: erosione inflazione (rosso)
                  {
                    label: 'Erosione inflazione (-2%/anno)',
                    data: chartData.inflVals,
                    borderColor: '#c62828',
                    backgroundColor: 'rgba(198,40,40,0.06)',
                    tension: 0.3,
                    fill: false,
                    borderWidth: 2,
                    pointRadius: 1,
                    borderDash: [4, 4],
                  },
                  // Deployment 22 — Sezione H: conto deposito (blu)
                  {
                    label: 'Conto deposito (2%)',
                    data: chartData.liqVals,
                    borderColor: '#1565c0',
                    backgroundColor: 'rgba(21,101,192,0.06)',
                    tension: 0.3,
                    fill: false,
                    borderWidth: 2,
                    pointRadius: 1,
                  },
                ],
              }}
              options={{
                responsive: true, maintainAspectRatio: false,
                plugins: { legend: { labels: { color: '#888780' } }, tooltip: { callbacks: { label: (c) => `${c.dataset.label}: € ${fmt(c.parsed.y)}` } } },
                scales: {
                  x: { ticks: { color: '#888780' }, grid: { color: 'rgba(255,255,255,0.04)' } },
                  y: { ticks: { color: '#888780', callback: (v) => '€ ' + fmt(v) }, grid: { color: 'rgba(255,255,255,0.04)' } },
                },
              }}
            />
          </div>
          <div className="mt-3 text-sm text-[#E8E8E4]" data-testid="hist-final-value">
            Valore finale stimato: <strong style={{ color: '#2e7d32' }}>€ {fmt(finalValue)}</strong>
          </div>
          {/* Deployment 23 — Sezione E / Deployment 24 — Sezione C: riquadro motivazionale "sotto il materasso".
              Rendering condizionale: portfolio non vuoto, differenza valida e positiva. */}
          {(() => {
            const valoreInflazione = chartData.inflVals[chartData.inflVals.length - 1];
            const differenzaAssoluta = finalValue - valoreInflazione;
            const show = portfolio.length > 0
              && !isNaN(differenzaAssoluta)
              && differenzaAssoluta > 0;
            if (!show) return null;
            const differenzaPercentuale = ((differenzaAssoluta / valoreInflazione) * 100).toFixed(1);
            return (
              <div
                className="mt-4 p-4 rounded-xl text-sm leading-relaxed"
                style={{
                  background: '#e8f5e9',
                  border: '1px solid #2e7d32',
                  color: '#1b3a1f',
                }}
                data-testid="hist-mattress-box"
              >
                <p className="mb-2">
                  💡 Se avessi tenuto <strong>€ {fmt(amount)}</strong> fermi, oggi varrebbero <strong>€ {fmt(valoreInflazione)}</strong> reali
                  <span style={{ opacity: 0.8 }}> (l'inflazione al 2%/anno ne erode il potere d'acquisto).</span>
                </p>
                <p className="mb-2">
                  Investendo nel tuo portafoglio, proietti <strong>€ {fmt(finalValue)}</strong>.
                </p>
                <p style={{ fontWeight: 600 }}>
                  Differenza: +€ {fmt(differenzaAssoluta)} (+{differenzaPercentuale}%)
                </p>
              </div>
            );
          })()}
          {realReturn && (
            <p className="text-xs text-[#888780] mt-2 italic" data-testid="hist-real-note">
              Rendimento reale calcolato al netto dell'inflazione stimata al 2% annuo.
            </p>
          )}
          <p className="text-xs text-[#888780] mt-3 italic">
            {realReturn
              ? <>I rendimenti mostrati sono <strong>reali</strong> (netto inflazione 2%). Il rendimento nominale è <strong>{nominalCagrPct}%/anno</strong>.</>
              : <>I rendimenti mostrati sono <strong>nominali</strong>. Il rendimento reale al netto dell'inflazione media del 2% annuo è circa <strong>{realCagrPctNet}%/anno</strong>.</>}
          </p>
          <p className="text-xs text-[#888780] mt-2 italic">
            I rendimenti storici mostrati sono medie annualizzate su dati pubblici. Le performance passate non garantiscono risultati futuri. Fonte dati: indici pubblici al 31/12/{currentYear}.
          </p>
          <p className="text-xs text-[#888780] mt-1 italic">
            Dati storici disponibili fino al 31/12/{currentYear} — aggiornamento annuale previsto.
          </p>
        </>
      )}
    </CollapsibleBox>
  );
};

// ── VAR box ─────────────────────────────────────────────────────────────
export const VARRiskBox = ({ portfolio, analytics, isOpen, onToggle }) => {
  if (!analytics || !portfolio.length) {
    return (
      <CollapsibleBox title="📊 Stima del rischio annuo" isOpen={isOpen} onToggle={onToggle}>
        <p className="text-sm text-[#888780] text-center py-6" data-testid="var-empty">
          Aggiungi almeno uno strumento per vedere la stima del rischio.
        </p>
      </CollapsibleBox>
    );
  }

  const var95 = analytics.total * analytics.wVol * 1.645;
  const varRatio = var95 / analytics.total;

  let message;
  let color = '#E8E8E4';
  if (varRatio > 1) {
    color = '#C0392B';
    message = (
      <div className="text-sm" style={{ color }} data-testid="var-extreme-msg">
        <AlertTriangle className="w-4 h-4 inline-block mr-1" />
        VAR &gt; 100% del portafoglio — questo portafoglio ha un rischio estremo. Considera una maggiore diversificazione.
      </div>
    );
  } else if (varRatio < 0.01) {
    color = '#3D7A52';
    message = (
      <div className="text-sm" style={{ color }} data-testid="var-lowrisk-msg">
        ✅ Rischio molto basso — portafoglio prevalentemente in liquidità o obbligazionario a breve termine.
      </div>
    );
  } else {
    message = null;
  }

  return (
    <CollapsibleBox title="📊 Stima del rischio annuo" isOpen={isOpen} onToggle={onToggle} testid="var-box">
      <p className="text-xs text-[#888780] mb-3">Value at Risk al 95%</p>
      <div className="text-3xl font-bold text-[#E8E8E4] mb-2" data-testid="var-value" style={{ color }}>
        VAR stimato: € {fmt(var95)}
      </div>
      {message}
      <p className="text-xs text-[#888780] mt-4 leading-relaxed">
        Il <strong>VAR (Value at Risk)</strong> al 95% stima la perdita massima probabile del tuo portafoglio in un anno normale.
        Esempio: un VAR di €5.000 significa che nel 95% degli scenari non perderai più di €5.000 in un anno.
        Nel restante 5% degli scenari la perdita potrebbe essere superiore.
      </p>
      <p className="text-xs mt-3">
        <Link to="/glossario?term=var" className="text-[#C9A84C] hover:underline" data-testid="var-glossary-link">
          → Scopri cos'è il VAR nel glossario
        </Link>
      </p>
    </CollapsibleBox>
  );
};

// ── Shared collapsible box (mobile-friendly) ─────────────────────────────
const CollapsibleBox = ({ title, isOpen, onToggle, children, testid }) => (
  <div
    style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: '16px 20px' }}
    data-testid={testid}
  >
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between text-left"
      aria-expanded={isOpen}
    >
      <span className="text-sm font-medium text-[#E8E8E4]">{title}</span>
      {isOpen ? <ChevronDown className="w-4 h-4 text-[#888780]" /> : <ChevronRight className="w-4 h-4 text-[#888780]" />}
    </button>
    {isOpen && <div className="mt-4">{children}</div>}
  </div>
);
